// SPDX-License-Identifier: Elastic-2.0
/**
 * formatProposedRoutine — derive plain-language "trigger" + "action" copy
 * from a `Suggestion.proposed_routine` payload (T-025).
 *
 * The F-300 `Suggestion.proposed_routine` field is typed as
 * `Record<string, any>` in the OpenAPI client because the gateway only
 * promises an opaque routine specification at MVP. In practice the
 * router we're building toward (F-300/F-213) ships:
 *
 *   {
 *     trigger: { summary: "When you leave home (no one's phone is here for 10+ minutes)", ... },
 *     action:  { summary: "Set Living Room thermostat to 16°C", ... },
 *   }
 *
 * We prefer the `.summary` strings when present — they're already
 * voice-and-tone correct. When absent (older payloads, smoke fixtures)
 * we render a generic key/value dump so the user sees *something*
 * instead of an empty modal. That dump isn't pretty, but it's better
 * than silence and is good-enough until the v0.2 polish pass adds
 * human-language formatting for richer schemas.
 *
 * Pure function — no React, no i18n — so it's trivially unit-testable
 * and reusable by the future Suggestions page (T-027) if needed. The
 * "no details" fallback uses an i18n key (`suggestion_modal.generic_no_details`)
 * so callers can localise the empty string; we return the key, not the
 * translated string, to keep this util free of `react-i18next`.
 */

/** I18n key for "(no details)" — exported so callers translate consistently. */
export const NO_DETAILS_KEY = "suggestion_modal.generic_no_details";

/**
 * Shape of the result returned by `formatProposedRoutine`.
 *
 * `trigger` / `action` are either a user-ready sentence (when the
 * payload carried `.summary`) or a generic key/value dump (one entry
 * per line) — the modal renders both with `white-space: pre-line` so
 * the line breaks survive.
 *
 * When the relevant sub-field is missing entirely we return the
 * `NO_DETAILS_KEY` i18n key — the caller is expected to pass it through
 * `t(...)` before rendering. The marker prefix `i18n:` keeps the
 * "this is a key, not text" intent obvious at the callsite.
 */
export interface ProposedRoutineSummary {
  /** Pre-formatted trigger sentence OR a generic key/value dump OR an i18n key. */
  trigger: string;
  /** Pre-formatted action sentence OR a generic key/value dump OR an i18n key. */
  action: string;
  /**
   * `true` when the corresponding field falls back to the i18n
   * `NO_DETAILS_KEY` and the caller should run it through `t(...)`.
   * Indexed by field so the caller can decide per row.
   */
  fallback: { trigger: boolean; action: boolean };
}

/**
 * Generic object → "key: <json>" dump, one entry per line. Used when
 * the trigger/action sub-object doesn't carry a `.summary` field.
 *
 * `null` / `undefined` collapse to the i18n no-details key — callers
 * read `fallback.trigger` / `fallback.action` on the result to know
 * whether to translate.
 */
function renderGeneric(obj: unknown): { text: string; fallback: boolean } {
  if (obj === undefined || obj === null) {
    return { text: NO_DETAILS_KEY, fallback: true };
  }
  if (typeof obj !== "object") {
    // Primitive — render as-is. Should be rare; the contract says object.
    return { text: String(obj), fallback: false };
  }
  const entries = Object.entries(obj as Record<string, unknown>);
  if (entries.length === 0) {
    return { text: NO_DETAILS_KEY, fallback: true };
  }
  const text = entries
    .map(([k, v]) => `${k}: ${typeof v === "string" ? v : JSON.stringify(v)}`)
    .join("\n");
  return { text, fallback: false };
}

/**
 * Best-effort extractor for `.summary` from an unknown payload shape.
 * Returns `null` when the sub-object isn't present OR its `summary`
 * isn't a non-empty string — falling through to the generic dump.
 */
function pickSummary(sub: unknown): string | null {
  if (sub === undefined || sub === null || typeof sub !== "object") return null;
  const summary = (sub as { summary?: unknown }).summary;
  return typeof summary === "string" && summary.trim().length > 0 ? summary : null;
}

/**
 * Derive human-readable trigger + action summaries from an opaque
 * `proposed_routine` payload. Always returns both fields — never
 * throws and never returns `null`, even for `{}` input.
 *
 * @param routine The opaque `Suggestion.proposed_routine` payload (may
 *                be `null` / `undefined` if the upstream relaxes types).
 */
export function formatProposedRoutine(
  routine: Record<string, unknown> | null | undefined,
): ProposedRoutineSummary {
  const trigger = (routine as { trigger?: unknown } | null | undefined)?.trigger;
  const action = (routine as { action?: unknown } | null | undefined)?.action;

  const triggerSummary = pickSummary(trigger);
  const actionSummary = pickSummary(action);

  if (triggerSummary && actionSummary) {
    return {
      trigger: triggerSummary,
      action: actionSummary,
      fallback: { trigger: false, action: false },
    };
  }

  const triggerOut = triggerSummary
    ? { text: triggerSummary, fallback: false }
    : renderGeneric(trigger);
  const actionOut = actionSummary
    ? { text: actionSummary, fallback: false }
    : renderGeneric(action);

  return {
    trigger: triggerOut.text,
    action: actionOut.text,
    fallback: { trigger: triggerOut.fallback, action: actionOut.fallback },
  };
}
